/**
 * Manufacturing management → Batch details (parity with Manufacturing Dashboard **Batch Details**).
 *
 * Master–detail: batch list on the left, line detail on the right.
 * `?batch_id=` selects a batch. `?search=` / `?search_batch=` filter the list. Clicking a row selects only.
 * **Edit batch** uses the same URL as Incoming orders **Add to batch**.
 */

import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import {
  BatchCard,
  type ProductionCalendarBatch,
  type ProductionCalendarStation,
} from "@/pages/manufacturing/ProductionCalendarPage";
import { AssignAbbPddLabelsModal } from "@/pages/manufacturing/AssignAbbPddLabelsModal";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  CircleAlert,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Pencil,
  RefreshCw,
  XCircle,
} from "lucide-react";

import { EntityPeekHint } from "@/components/hub/EntityPeekHint";
import { SalesOrderDetailLink, ShipmentDetailLink } from "@/components/hub/HubEntityDetailLink";
import { MrpSkuWithUnitLabel } from "@/components/mrp/MrpSkuWithUnitLabel";
import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { BatchOnHoldControls } from "@/components/manufacturing/BatchOnHoldControls";
import { BatchTeamNotesPanel } from "@/components/manufacturing/BatchTeamNotesPanel";
import { MrpSkuCatalogHoverHintBody } from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, TextArea } from "@/components/ui/Input";
import {
  CardTitleWithHelp,
  HoverHintContent,
  HoverHintTrigger,
  HoverHintWithGlyph,
  PageTitleWithHelp,
} from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { buildManufacturingEditBatchPathForBatch } from "@/lib/manufacturingEditBatchPath";
import {
  batchDetailsSearchFromUrlParams,
  batchDetailsSearchIsExactBatchIdFilter,
  batchDetailsServerSearchTerm,
  filterBatchDetailsRows,
} from "@/lib/batchDetailsSearch";
import {
  compareBatchDetailsSerialRows,
  type BatchDetailsSerialsSortDir,
  type BatchDetailsSerialsSortKey,
} from "@/lib/batchDetailsSerialsSort";
import { formatShipToBlock } from "@/lib/formatShipToBlock";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import { MRP_LOCATION_PUBLIC_LOCATION_ID, type MrpLocation, type MrpSkuHoverHintFields } from "@/lib/mrp";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";
import { isAbbPddLabelSku } from "@integrator-src/manufacturing/configuration-sku-family";

const BATCH_DETAILS_SITE_KEYS = ["sgs", "suntronic"] as const satisfies readonly MrpLocation[];

function locationParamFromSiteKeys(keys: ReadonlySet<MrpLocation>): string {
  return [...keys].map((key) => String(MRP_LOCATION_PUBLIC_LOCATION_ID[key])).join(",");
}

interface BatchListRow {
  batch_id: number;
  date_created: string;
  mfr_ship_by_date: string | null;
  ship_to_recipient: string | null;
  manufacturer_sales_order_number: string | null;
  gso_numbers: string[];
  end_user_names: string[];
  location_id: number;
  location_label: string;
  shipping_status?: "not_shipped" | "partially_shipped" | "shipped";
  manufacturing_started?: boolean;
  shipment_on_hold?: boolean;
}

interface BatchListApi {
  rows: BatchListRow[];
}

interface BatchQuickSearchApi {
  rows: Array<Omit<BatchListRow, "date_created" | "location_id">>;
}

interface BatchDetailsSalesOrderLineContext {
  sku: string;
  quantity: number;
}

interface BatchDetailsSalesOrderContext {
  sales_order_id: number;
  sales_order_number: string | null;
  client_name: string | null;
  end_user_name: string | null;
  customer_purchase_order_number: string | null;
  sgs_ship_by_date: string | null;
  lines: BatchDetailsSalesOrderLineContext[];
}

interface BatchDetailLine {
  batch_item_id: number;
  build_order_id: number | null;
  build_order_number: string | null;
  sku: string;
  batch_item_quantity: number;
  quantity_shipped: number;
  quantity_assembled: number;
  sales_order_number: string | null;
  sales_order_id: number | null;
  client_name: string | null;
  end_user_name: string | null;
  sgs_ship_by_date: string | null;
  shipment_id: number | null;
  shipment_date_shipped: string | null;
  tla_build_order_id: number | null;
  tla_build_order_number: string | null;
  tla_purchase_order_number: string | null;
  has_pdd_labels: boolean;
  pdd_sku: string | null;
  pdd_catalog: string | null;
}

type ProductionCalendarBatchByIdResponse =
  | { found: false }
  | { found: true; eligible: false; reason: string }
  | {
      found: true;
      eligible: true;
      timeZone: string;
      weekStart: string;
      weekEnd: string;
      stations: ProductionCalendarStation[];
      batch: ProductionCalendarBatch;
    };

interface BatchDetailApi {
  batch_id: number;
  date_created: string;
  mfr_ship_by_date: string | null;
  manufacturer_sales_order_number: string | null;
  mfr_remark: string | null;
  ship_to_recipient: string | null;
  ship_to_address_line_1: string | null;
  ship_to_address_line_2: string | null;
  ship_to_address_line_3: string | null;
  ship_to_city: string | null;
  ship_to_state: string | null;
  ship_to_zip: string | null;
  ship_to_country: string | null;
  location_id: number;
  location_label: string;
  serial_count: number;
  lines: BatchDetailLine[];
  sales_order_ids: number[];
  sales_order_contexts: Record<string, BatchDetailsSalesOrderContext>;
  shipment_on_hold: boolean;
}

interface BatchSerialRow {
  serial_fci_id: number;
  device_label_serial: string | null;
  finished_sku: string | null;
  batch_line_sku: string | null;
  clamp_serial: string | null;
  pcba_serial: string | null;
  box_serial: string | null;
  station_id: number | null;
  station_name: string | null;
  shipment_id: number | null;
  carton_number: number | null;
  imei: string | null;
  wireless_portal_org_name: string | null;
  wireless_portal_group_names: string | null;
}

interface BatchSerialsApi {
  rows: BatchSerialRow[];
}

interface BatchSerialsPortalApi {
  by_imei: Record<
    string,
    {
      wireless_portal_org_name: string | null;
      wireless_portal_group_names: string | null;
      wireless_portal_fci_serial: string | null;
      wireless_portal_imei: string | null;
      wireless_portal_shipment_date: string | null;
    }
  >;
  alignment_by_serial_fci_id: Record<
    string,
    {
      aligned: boolean;
      discrepancies: string[];
    }
  >;
}

const PORTAL_COLUMNS_BLUR_DELAY_MS = 1000;
const WIRELESS_PROD_INACCESSIBLE_IN_DEV_MESSAGE =
  "The Wireless_Prod database is inaccessible in local development. Portal org and group load on the production hub only.";

function wirelessPortalDeviceDetailsUrl(imei: string): string {
  return `https://portal-sgs.com/device-details/?id=${encodeURIComponent(imei.trim())}`;
}

function batchSerialFinishedSkuMismatch(
  batchLineSku: string | null | undefined,
  finishedSku: string | null | undefined,
): boolean {
  const batchLine = (batchLineSku ?? "").trim();
  const finished = (finishedSku ?? "").trim();
  if (batchLine === "" && finished === "") return false;
  return batchLine !== finished;
}

function batchSerialPortalFieldCell(
  value: string | null | undefined,
  options: {
    rowHasImei: boolean;
    pending: boolean;
    failed: boolean;
    errorMessage: string;
    fieldLabel: string;
  },
): ReactNode {
  if (options.pending) {
    return "Loading…";
  }
  if (options.failed && options.rowHasImei) {
    return (
      <HoverHintTrigger
        trigger={
          <CircleAlert
            className="h-3.5 w-3.5 shrink-0 text-red-600"
            aria-label={`${options.fieldLabel} could not be loaded from wireless portal`}
          />
        }
      >
        {options.errorMessage}
      </HoverHintTrigger>
    );
  }
  return value != null && value.trim() !== "" ? value : "—";
}

function batchSerialPortalAlignmentCell(
  alignment: { aligned: boolean; discrepancies: string[] } | undefined,
  options: {
    rowHasImei: boolean;
    pending: boolean;
    failed: boolean;
    errorMessage: string;
  },
): ReactNode {
  if (!options.rowHasImei) {
    return "—";
  }
  if (options.pending) {
    return "Loading…";
  }
  if (options.failed) {
    return (
      <HoverHintTrigger
        trigger={
          <CircleAlert
            className="h-3.5 w-3.5 shrink-0 text-red-600"
            aria-label="Portal match could not be loaded from wireless portal"
          />
        }
      >
        {options.errorMessage}
      </HoverHintTrigger>
    );
  }
  if (alignment == null) {
    return (
      <HoverHintTrigger
        trigger={
          <XCircle
            className="h-3.5 w-3.5 shrink-0 text-red-600"
            aria-label="Portal match unavailable for this serial row"
          />
        }
      >
        Portal match could not be resolved for this serial row. Refresh portal or reload serials if the batch changed
        while this panel was open.
      </HoverHintTrigger>
    );
  }
  if (alignment.aligned) {
    return (
      <HoverHintTrigger
        trigger={
          <CheckCircle2
            className="h-3.5 w-3.5 shrink-0 text-emerald-600"
            aria-label="Portal IMEI and serial match serial_fci and device is marked shipped"
          />
        }
      >
        Portal IMEI and serial match serial_fci, portal Status is Shipped, and ShipmentDate is on or before today.
      </HoverHintTrigger>
    );
  }
  return (
    <HoverHintTrigger
      trigger={
        <XCircle
          className="h-3.5 w-3.5 shrink-0 text-red-600"
          aria-label="Portal IMEI, serial, or shipped status does not match serial_fci"
        />
      }
    >
      {alignment.discrepancies.join(" ")}
    </HoverHintTrigger>
  );
}

function formatShortDate(isoDay: string | null | undefined): string {
  if (isoDay == null || isoDay.trim() === "") return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDay.trim());
  if (match == null) return isoDay.trim();
  const [, yyyy, mm, dd] = match;
  return `${mm}-${dd}-${yyyy}`;
}

function batchDetailsSalesOrderHoverHint(
  context: BatchDetailsSalesOrderContext | undefined,
  fallbackLine: BatchDetailLine,
): ReactNode {
  return (
    <EntityPeekHint
      kind="sales_order"
      context={
        context ?? {
          sales_order_number: fallbackLine.sales_order_number,
          client_name: fallbackLine.client_name,
          end_user_name: fallbackLine.end_user_name,
          sgs_ship_by_date: fallbackLine.sgs_ship_by_date,
        }
      }
    />
  );
}

function toHtmlDateValue(raw: string | null | undefined): string {
  if (raw == null || String(raw).trim() === "") return "";
  const slice = String(raw).trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/u.test(slice) ? slice : "";
}

type BatchHeaderSaveField = "mfr_ship_by_date" | "mfr_remark";

function formatCreatedAt(raw: string): string {
  const parsed = Date.parse(raw);
  if (!Number.isFinite(parsed)) return raw;
  return new Date(parsed).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function BatchHeaderEditableDd({
  label,
  value,
  display,
  inputKind,
  canEdit,
  savePending,
  editAriaLabel,
  onSave,
}: {
  label: string;
  value: string;
  display: ReactNode;
  inputKind: "text" | "date" | "multiline";
  canEdit: boolean;
  savePending: boolean;
  editAriaLabel: string;
  onSave: ((next: string | null) => void) | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!editing) {
      setDraft(value);
    }
  }, [value, editing]);

  const commit = (): void => {
    if (onSave == null) {
      setEditing(false);
      return;
    }
    const trimmed = draft.trim();
    const next: string | null = trimmed === "" ? null : trimmed;
    const current = value.trim() === "" ? null : value.trim();
    if (next !== current) {
      onSave(next);
    }
    setEditing(false);
  };

  const pencilButton =
    onSave != null ? (
      <HoverHintTrigger
        trigger={
          <button
            type="button"
            onClick={() => setEditing(true)}
            disabled={savePending}
            className="shrink-0 rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
            aria-label={editAriaLabel}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
        }
      >
        {editAriaLabel}
      </HoverHintTrigger>
    ) : null;

  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1">
        {editing && onSave != null ? (
          <div className="flex min-w-0 items-start gap-2">
            {inputKind === "date" ? (
              <Input
                type="date"
                className="h-10 max-w-[12rem]"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    (event.currentTarget as HTMLInputElement).blur();
                  }
                  if (event.key === "Escape") {
                    setDraft(value);
                    setEditing(false);
                  }
                }}
                disabled={savePending}
                autoFocus
              />
            ) : inputKind === "multiline" ? (
              <TextArea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setDraft(value);
                    setEditing(false);
                  }
                }}
                disabled={savePending}
                autoFocus
                rows={3}
                className="min-h-[4.5rem] flex-1 text-sm"
              />
            ) : (
              <Input
                type="text"
                className="h-10 min-w-0 flex-1"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    (event.currentTarget as HTMLInputElement).blur();
                  }
                  if (event.key === "Escape") {
                    setDraft(value);
                    setEditing(false);
                  }
                }}
                disabled={savePending}
                autoFocus
              />
            )}
            {savePending ? <Loader2 className="mt-2 h-4 w-4 shrink-0 animate-spin text-slate-500" aria-hidden /> : null}
          </div>
        ) : (
          <div className="flex min-w-0 items-start gap-1.5">
            <span className="min-w-0 flex-1">{display}</span>
            {canEdit ? pencilButton : null}
          </div>
        )}
      </dd>
    </div>
  );
}

type BatchListSortKey = "batch" | "site" | "ship_by" | "status";
type BatchListSortDir = "asc" | "desc";

const BATCH_LIST_SHIPPING_SORT_RANK: Record<NonNullable<BatchListRow["shipping_status"]>, number> = {
  not_shipped: 0,
  partially_shipped: 1,
  shipped: 2,
};

function compareBatchListRows(
  left: BatchListRow,
  right: BatchListRow,
  sortKey: BatchListSortKey,
  sortDir: BatchListSortDir,
): number {
  let cmp = 0;
  switch (sortKey) {
    case "batch":
      cmp = left.batch_id - right.batch_id;
      break;
    case "site":
      cmp =
        left.location_label.localeCompare(right.location_label, undefined, { sensitivity: "base" }) ||
        left.location_id - right.location_id;
      break;
    case "ship_by": {
      const leftDate = left.mfr_ship_by_date ?? "";
      const rightDate = right.mfr_ship_by_date ?? "";
      cmp = leftDate.localeCompare(rightDate) || left.batch_id - right.batch_id;
      break;
    }
    case "status":
      cmp =
        (left.shipping_status == null ? -1 : BATCH_LIST_SHIPPING_SORT_RANK[left.shipping_status]) -
        (right.shipping_status == null ? -1 : BATCH_LIST_SHIPPING_SORT_RANK[right.shipping_status]);
      if (cmp === 0) {
        cmp = Number(left.manufacturing_started) - Number(right.manufacturing_started);
      }
      if (cmp === 0) {
        cmp = left.batch_id - right.batch_id;
      }
      break;
  }
  return sortDir === "asc" ? cmp : -cmp;
}

function CompactSortTh<T extends string,>({
  column,
  children,
  sortKey,
  sortDir,
  onSort,
  className,
}: {
  column: T;
  children: ReactNode;
  sortKey: T;
  sortDir: "asc" | "desc";
  onSort: (key: T) => void;
  className?: string;
}): JSX.Element {
  const active = sortKey === column;
  return (
    <th
      scope="col"
      className={cn("bg-white align-top", className)}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="group inline-flex w-full min-w-0 items-center gap-0.5 font-inherit uppercase tracking-wide text-inherit hover:text-slate-800"
      >
        {children}
        <span
          className={cn("shrink-0 text-[10px] leading-none text-slate-500", !active && "opacity-0 group-hover:opacity-70")}
          aria-hidden
        >
          {active ? (sortDir === "asc" ? "▲" : "▼") : "▲"}
        </span>
      </button>
    </th>
  );
}

function BatchListSortTh({
  column,
  children,
  sortKey,
  sortDir,
  onSort,
  className,
}: {
  column: BatchListSortKey;
  children: ReactNode;
  sortKey: BatchListSortKey;
  sortDir: BatchListSortDir;
  onSort: (key: BatchListSortKey) => void;
  className?: string;
}): JSX.Element {
  return (
    <CompactSortTh
      column={column}
      sortKey={sortKey}
      sortDir={sortDir}
      onSort={onSort}
      className={cn("py-2", className)}
    >
      {children}
    </CompactSortTh>
  );
}

function batchStatusBadges(row: BatchListRow): JSX.Element {
  if (row.shipping_status == null) {
    return (
      <Badge tone="muted" className="whitespace-nowrap px-1.5 text-[10px]">
        Search result
      </Badge>
    );
  }

  const shippingBadge = row.shipment_on_hold ? (
    <Badge tone="warning" className="whitespace-nowrap px-1.5 text-[10px]">
      Shipment hold
    </Badge>
  ) : row.shipping_status === "shipped" ? (
    <Badge tone="success" className="whitespace-nowrap px-1.5 text-[10px]">
      Shipped
    </Badge>
  ) : row.shipping_status === "partially_shipped" ? (
    <Badge tone="warning" className="whitespace-nowrap px-1.5 text-[10px]">
      Partially shipped
    </Badge>
  ) : (
    <Badge tone="muted" className="whitespace-nowrap px-1.5 text-[10px]">
      Not shipped
    </Badge>
  );

  return (
    <div className="flex flex-nowrap items-center gap-1">
      {shippingBadge}
      {row.manufacturing_started ? (
        <Badge tone="neutral" className="whitespace-nowrap px-1.5 text-[10px]">
          Started
        </Badge>
      ) : (
        <Badge tone="muted" className="whitespace-nowrap px-1.5 text-[10px]">
          Not started
        </Badge>
      )}
    </div>
  );
}

export function BatchDetailsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const [searchParams, setSearchParams] = useSearchParams();
  const batchIdParam = searchParams.get("batch_id") ?? "";
  const searchParam = searchParams.get("search") ?? "";
  const searchBatchParam = searchParams.get("search_batch") ?? "";
  const [searchText, setSearchText] = useState(() =>
    batchDetailsSearchFromUrlParams({
      search: searchParam,
      searchBatch: searchBatchParam,
    }),
  );
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
  const [batchListSortKey, setBatchListSortKey] = useState<BatchListSortKey>("ship_by");
  const [batchListSortDir, setBatchListSortDir] = useState<BatchListSortDir>("desc");
  const [serialsOpen, setSerialsOpen] = useState(false);
  const [pddAssignLine, setPddAssignLine] = useState<BatchDetailLine | null>(null);
  const [serialsSortKey, setSerialsSortKey] = useState<BatchDetailsSerialsSortKey>("station");
  const [serialsSortDir, setSerialsSortDir] = useState<BatchDetailsSerialsSortDir>("asc");
  const [portalColumnsBlurred, setPortalColumnsBlurred] = useState(false);
  const [locationFilter, setLocationFilter] = useState<Set<MrpLocation>>(
    () => new Set(BATCH_DETAILS_SITE_KEYS),
  );
  const selectedBatchId = useMemo(() => {
    const n = Number.parseInt(batchIdParam.trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [batchIdParam]);

  useEffect(() => {
    setSearchText(
      batchDetailsSearchFromUrlParams({
        search: searchParam,
        searchBatch: searchBatchParam,
      }),
    );
  }, [searchBatchParam, searchParam]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearchText(searchText), 200);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  const locationQuery = useMemo(
    () => locationParamFromSiteKeys(locationFilter),
    [locationFilter],
  );

  const listQuery = useQuery<BatchListApi, ApiError>({
    queryKey: ["manufacturing", "batch-details", "batches", locationQuery],
    queryFn: () =>
      apiFetch<BatchListApi>("/api/manufacturing/batch-details/batches", {
        query: { location: locationQuery },
      }),
    enabled: locationFilter.size > 0 && searchText.trim() === "",
  });

  const serverSearchTerm = batchDetailsServerSearchTerm(debouncedSearchText);
  const quickSearchQuery = useQuery<BatchQuickSearchApi, ApiError>({
    queryKey: ["manufacturing", "batch-details", "quick-search", serverSearchTerm],
    queryFn: () =>
      apiFetch<BatchQuickSearchApi>("/api/manufacturing/batches/quick-search", {
        query: { q: serverSearchTerm, limit: 24 },
      }),
    enabled: locationFilter.size > 0 && serverSearchTerm.length >= 2,
  });

  const detailQuery = useQuery<BatchDetailApi, ApiError>({
    queryKey: ["manufacturing", "batch-details", "batch", selectedBatchId],
    queryFn: () =>
      apiFetch<BatchDetailApi>(`/api/manufacturing/batch-details/batch/${String(selectedBatchId)}`),
    enabled: selectedBatchId != null,
  });

  useEffect(() => {
    setSerialsOpen(false);
    setSerialsSortKey("station");
    setSerialsSortDir("asc");
  }, [selectedBatchId]);

  const serialsQuery = useQuery<BatchSerialsApi, ApiError>({
    queryKey: ["manufacturing", "batch-details", "serials", selectedBatchId],
    queryFn: () =>
      apiFetch<BatchSerialsApi>(
        `/api/manufacturing/batch-details/batch/${String(selectedBatchId)}/serials`,
      ),
    enabled: selectedBatchId != null,
  });

  const serialsHasImei = useMemo(
    () =>
      (serialsQuery.data?.rows ?? []).some((row) => (row.imei ?? "").trim() !== ""),
    [serialsQuery.data?.rows],
  );

  const portalLookupDevBlocked = import.meta.env.DEV && serialsHasImei;

  const serialsPortalQuery = useQuery<BatchSerialsPortalApi, ApiError>({
    queryKey: ["manufacturing", "batch-details", "serials-portal", selectedBatchId],
    queryFn: () =>
      apiFetch<BatchSerialsPortalApi>(
        `/api/manufacturing/batch-details/batch/${String(selectedBatchId)}/serials/portal`,
      ),
    enabled: selectedBatchId != null && serialsHasImei && !import.meta.env.DEV,
  });

  useEffect(() => {
    if (portalLookupDevBlocked || !serialsHasImei || !serialsPortalQuery.isFetching) {
      setPortalColumnsBlurred(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setPortalColumnsBlurred(true);
    }, PORTAL_COLUMNS_BLUR_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [portalLookupDevBlocked, serialsHasImei, serialsPortalQuery.isFetching, serialsPortalQuery.fetchStatus]);

  const displaySerialRows = useMemo((): BatchSerialRow[] => {
    const baseRows = serialsQuery.data?.rows ?? [];
    const portalByImei = serialsPortalQuery.data?.by_imei ?? {};
    return baseRows.map((row) => {
      const imei = (row.imei ?? "").trim();
      const portal = imei !== "" ? portalByImei[imei] : undefined;
      return {
        ...row,
        wireless_portal_org_name: portal?.wireless_portal_org_name ?? null,
        wireless_portal_group_names: portal?.wireless_portal_group_names ?? null,
      };
    });
  }, [serialsQuery.data?.rows, serialsPortalQuery.data?.by_imei]);

  const portalAlignmentBySerialFciId = serialsPortalQuery.data?.alignment_by_serial_fci_id ?? {};

  const sortedDisplaySerialRows = useMemo(() => {
    const rows = [...displaySerialRows];
    rows.sort((left, right) =>
      compareBatchDetailsSerialRows(
        left,
        right,
        serialsSortKey,
        serialsSortDir,
        portalAlignmentBySerialFciId,
      ),
    );
    return rows;
  }, [displaySerialRows, portalAlignmentBySerialFciId, serialsSortDir, serialsSortKey]);

  const onSerialsSort = useCallback((key: BatchDetailsSerialsSortKey): void => {
    if (key === serialsSortKey) {
      setSerialsSortDir((direction) => (direction === "asc" ? "desc" : "asc"));
      return;
    }
    setSerialsSortKey(key);
    setSerialsSortDir("asc");
  }, [serialsSortKey]);

  const portalColumnsPending =
    serialsHasImei && portalColumnsBlurred && serialsPortalQuery.isFetching;
  const portalColumnsFailed =
    serialsHasImei && (portalLookupDevBlocked || serialsPortalQuery.isError);
  const portalColumnsErrorMessage = portalLookupDevBlocked
    ? WIRELESS_PROD_INACCESSIBLE_IN_DEV_MESSAGE
    : serialsPortalQuery.error?.message ?? "Wireless portal org and group could not be loaded.";

  const calendarEntryQuery = useQuery<ProductionCalendarBatchByIdResponse, ApiError>({
    queryKey: ["manufacturing", "production-calendar", "batch", selectedBatchId],
    queryFn: () =>
      apiFetch<ProductionCalendarBatchByIdResponse>(
        `/api/manufacturing/production-calendar/batch/${String(selectedBatchId)}`,
      ),
    enabled: selectedBatchId != null,
    refetchOnWindowFocus: false,
  });

  const calendarStationsById = useMemo(() => {
    const data = calendarEntryQuery.data;
    if (data == null || !data.found || !data.eligible) {
      return new Map<number, ProductionCalendarStation>();
    }
    return new Map(data.stations.map((station) => [station.id, station]));
  }, [calendarEntryQuery.data]);

  const productionCalendarWeekLink = useMemo(() => {
    const data = calendarEntryQuery.data;
    if (data == null || !data.found || !data.eligible) return null;
    const params = new URLSearchParams({ startDate: data.weekStart, includeOverdue: "0" });
    return `/manufacturing-management/production-calendar?${params.toString()}`;
  }, [calendarEntryQuery.data]);

  const searchActive = searchText.trim() !== "";

  const filteredRows = useMemo(() => {
    const rows: BatchListRow[] = searchActive
      ? (quickSearchQuery.data?.rows ?? []).map((row) => ({
          ...row,
          date_created: "",
          location_id:
            row.location_label === "SGS"
              ? MRP_LOCATION_PUBLIC_LOCATION_ID.sgs
              : MRP_LOCATION_PUBLIC_LOCATION_ID.suntronic,
        }))
      : listQuery.data?.rows ?? [];
    const selectedLocations = new Set<string>(
      [...locationFilter].map((location) => (location === "sgs" ? "SGS" : "Suntronic")),
    );
    return filterBatchDetailsRows(rows, searchText).filter((row) =>
      selectedLocations.has(row.location_label),
    );
  }, [listQuery.data?.rows, locationFilter, quickSearchQuery.data?.rows, searchActive, searchText]);

  const sortedFilteredRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((left, right) => compareBatchListRows(left, right, batchListSortKey, batchListSortDir));
    return rows;
  }, [batchListSortDir, batchListSortKey, filteredRows]);

  const onBatchListSort = useCallback((key: BatchListSortKey): void => {
    if (key === batchListSortKey) {
      setBatchListSortDir((direction) => (direction === "asc" ? "desc" : "asc"));
      return;
    }
    setBatchListSortKey(key);
    setBatchListSortDir(key === "ship_by" || key === "batch" ? "desc" : "asc");
  }, [batchListSortKey]);

  const lineSkusForHoverHints = useMemo(() => {
    const lines = detailQuery.data?.lines ?? [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const line of lines) {
      const sku = line.sku.trim();
      if (sku === "" || seen.has(sku)) continue;
      seen.add(sku);
      out.push(sku);
    }
    return out.slice(0, 500);
  }, [detailQuery.data?.lines]);

  const skuHoverHintsQuery = useQuery<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }, ApiError>({
    queryKey: ["mrp", "sku-hover-hints", "batch-details", lineSkusForHoverHints.join("\n")],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: lineSkusForHoverHints.join(",") },
      }),
    enabled: lineSkusForHoverHints.length > 0,
    refetchOnWindowFocus: false,
  });

  const skuHoverHints = skuHoverHintsQuery.data?.sku_hover_hints;

  const editBatchPath =
    selectedBatchId != null ? buildManufacturingEditBatchPathForBatch(selectedBatchId) : null;

  const batchHoldEligible =
    detailQuery.data?.location_id === MRP_LOCATION_PUBLIC_LOCATION_ID.sgs;
  const batchShipmentOnHold = detailQuery.data?.shipment_on_hold === true;

  const batchShipToBlock = useMemo(() => {
    if (detailQuery.data == null) return null;
    return formatShipToBlock(detailQuery.data);
  }, [detailQuery.data]);

  const patchBatchHeaderMutation = useMutation<
    { ok: boolean },
    ApiError,
    { field: BatchHeaderSaveField; body: Record<string, string | null> }
  >({
    mutationFn: async ({ body }) => {
      if (selectedBatchId == null) {
        throw new Error("Select a batch first.");
      }
      return apiFetch<{ ok: boolean }>(`/api/manufacturing/edit-batch/batch/${String(selectedBatchId)}`, {
        method: "PATCH",
        body,
      });
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "batch-details"] });
      if (variables.field === "mfr_ship_by_date" && selectedBatchId != null) {
        await queryClient.invalidateQueries({
          queryKey: ["manufacturing", "production-calendar", "batch", selectedBatchId],
        });
      }
    },
  });

  const batchHeaderFieldSavePending = (field: BatchHeaderSaveField): boolean =>
    patchBatchHeaderMutation.isPending && patchBatchHeaderMutation.variables?.field === field;

  function saveBatchHeaderField(field: BatchHeaderSaveField, body: Record<string, string | null>): void {
    patchBatchHeaderMutation.mutate({ field, body });
  }

  function openBatch(batchId: number) {
    const next = new URLSearchParams(searchParams);
    next.set("batch_id", String(batchId));
    const listSearch = batchDetailsSearchFromUrlParams({
      search: next.get("search") ?? "",
      searchBatch: next.get("search_batch") ?? "",
    });
    const dropBatchIdFilter =
      batchDetailsSearchIsExactBatchIdFilter(listSearch, batchId) ||
      (selectedBatchId != null && batchDetailsSearchIsExactBatchIdFilter(listSearch, selectedBatchId));
    if (dropBatchIdFilter) {
      next.delete("search");
      next.delete("search_batch");
    }
    setSearchParams(next, { replace: true });
  }

  function updateSearchFilter(value: string) {
    setSearchText(value);
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    if (trimmed === "") {
      next.delete("search");
    } else {
      next.set("search", value);
    }
    next.delete("search_batch");
    setSearchParams(next, { replace: true });
  }

  function handleBatchRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, batchId: number) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openBatch(batchId);
    }
  }

  function toggleLocation(key: MrpLocation) {
    setLocationFilter((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  const listCountLabel = (
    <span className="font-normal text-slate-600">
      ({filteredRows.length}
      {!searchActive && listQuery.data != null ? ` of ${String(listQuery.data.rows.length)}` : ""})
    </span>
  );
  const activeListQuery = searchActive ? quickSearchQuery : listQuery;
  const searchTooShort = searchActive && serverSearchTerm.length < 2;

  const tabBreadcrumbLabel =
    selectedBatchId != null ? `Batch ${String(selectedBatchId)}` : "Batch details";

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <PageTitleWithHelp
          hoverHint="Browse manufacturing batches at SGS and Suntronic (Manufacturing Dashboard Batch Details parity). Click a batch in the list to see lines; use the pencil on manufacturer ship-by and remark to edit inline. On an ABB SKU line, manufacturing write access can Assign ABB labels from the PDD catch folder. Edit batch opens the same screen as Incoming orders Add to batch."
          breadcrumbLabel={tabBreadcrumbLabel}
        >
          Batch details
        </PageTitleWithHelp>
        {selectedBatchId != null ? (
          <div className="flex flex-wrap items-center gap-2">
            {detailQuery.data != null && batchHoldEligible ? (
              <BatchOnHoldControls
                batchId={selectedBatchId}
                shipmentOnHold={batchShipmentOnHold}
                hubWritesAllowed={hubWritesAllowed}
              />
            ) : null}
            {editBatchPath != null ? (
              <Button type="button" size="sm" variant="primary" onClick={() => navigate(editBatchPath)}>
                Edit batch
              </Button>
            ) : null}
          </div>
        ) : null}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700" htmlFor="batch-details-search">
                Search batches
              </label>
              <Input
                id="batch-details-search"
                type="search"
                className="h-10"
                placeholder="Batch id, batch:2050, @batch 2050, so:GSO-…, end-user:Acme, ship-to…"
                value={searchText}
                onChange={(event) => updateSearchFilter(event.target.value)}
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-700">Site</p>
              <div className="flex flex-wrap gap-2">
                {BATCH_DETAILS_SITE_KEYS.map((key) => {
                  const active = locationFilter.has(key);
                  return (
                    <Button
                      key={key}
                      type="button"
                      size="sm"
                      variant={active ? "primary" : "secondary"}
                      onClick={() => toggleLocation(key)}
                    >
                      {key === "sgs" ? "SGS" : "SUN"}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {locationFilter.size === 0 ? (
        <Card>
          <CardBody>
            <p className="text-sm text-slate-700">Select at least one site (SGS or SUN) to load batches.</p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[minmax(20rem,26rem)_minmax(0,1fr)] lg:items-stretch">
          <Card className="flex min-h-0 flex-col lg:max-h-[calc(100vh-14rem)]">
            <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle>
                Batches {listCountLabel}
              </CardTitle>
              {activeListQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" aria-hidden /> : null}
            </CardHeader>
            <CardBody className="flex min-h-0 flex-1 flex-col p-0">
              {(activeListQuery.isError || activeListQuery.isLoading || searchTooShort || (activeListQuery.data != null && filteredRows.length === 0 && !activeListQuery.isLoading)) ? (
                <div className="px-5 py-4">
                  {activeListQuery.isError ? (
                    <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                      {activeListQuery.error.message}
                    </p>
                  ) : null}
                  {activeListQuery.isLoading ? <p className="text-sm text-slate-600">Loading batches…</p> : null}
                  {searchTooShort ? <p className="text-sm text-slate-600">Enter at least two characters to search.</p> : null}
                  {activeListQuery.data != null && filteredRows.length === 0 && !activeListQuery.isLoading ? (
                    <p className="text-sm text-slate-600">No batches match these filters.</p>
                  ) : null}
                </div>
              ) : null}
              {sortedFilteredRows.length > 0 ? (
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <table className="w-full table-fixed border-collapse text-left text-xs text-slate-800">
                    <colgroup>
                      <col className="w-[3.25rem]" />
                      <col className="w-[4.75rem]" />
                      <col className="w-[5rem]" />
                      <col />
                    </colgroup>
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white text-[11px] font-semibold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_0_rgb(226_232_240)]">
                      <tr>
                        <BatchListSortTh
                          column="batch"
                          sortKey={batchListSortKey}
                          sortDir={batchListSortDir}
                          onSort={onBatchListSort}
                          className="px-3"
                        >
                          Batch
                        </BatchListSortTh>
                        <BatchListSortTh
                          column="site"
                          sortKey={batchListSortKey}
                          sortDir={batchListSortDir}
                          onSort={onBatchListSort}
                          className="px-1"
                        >
                          Site
                        </BatchListSortTh>
                        <BatchListSortTh
                          column="ship_by"
                          sortKey={batchListSortKey}
                          sortDir={batchListSortDir}
                          onSort={onBatchListSort}
                          className="px-1"
                        >
                          Ship by
                        </BatchListSortTh>
                        <BatchListSortTh
                          column="status"
                          sortKey={batchListSortKey}
                          sortDir={batchListSortDir}
                          onSort={onBatchListSort}
                          className="px-3"
                        >
                          Status
                        </BatchListSortTh>
                      </tr>
                    </thead>
                    <tbody>
                    {sortedFilteredRows.map((row) => {
                      const isSelected = selectedBatchId === row.batch_id;
                      return (
                        <tr
                          key={row.batch_id}
                          role="button"
                          tabIndex={0}
                          aria-selected={isSelected}
                          onClick={() => openBatch(row.batch_id)}
                          onKeyDown={(event) => handleBatchRowKeyDown(event, row.batch_id)}
                          className={cn(
                            "cursor-pointer border-b border-slate-50 last:border-none hover:bg-slate-50/90",
                            isSelected && "bg-sky-50 ring-1 ring-inset ring-sky-200",
                          )}
                        >
                          <td className="px-3 py-2 align-top font-mono tabular-nums font-medium text-slate-900">
                            {row.batch_id}
                          </td>
                          <td className="px-1 py-2 align-top">
                            <ManufacturingLocationChip
                              locationId={row.location_id}
                              fallbackLabel={row.location_label}
                            />
                          </td>
                          <td className="px-1 py-2 align-top whitespace-nowrap tabular-nums">
                            {formatShortDate(row.mfr_ship_by_date)}
                          </td>
                          <td className="px-3 py-2 align-top">{batchStatusBadges(row)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              ) : null}
            </CardBody>
          </Card>

          <div className="flex min-h-0 min-w-0 flex-col gap-4">
          <Card className="flex min-h-0 flex-col">
            {selectedBatchId == null ? (
              <CardBody className="flex flex-1 flex-col justify-center">
                <p className="text-sm text-slate-600">
                  Select a batch from the list to see ship-to, remarks, and line-level quantity.
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  To schedule new quantity, use{" "}
                  <Link className="font-medium text-sky-700 underline" to="/manufacturing-management/incoming-orders">
                    Incoming orders
                  </Link>
                  .
                </p>
              </CardBody>
            ) : (
              <>
                <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <CardTitle>Batch {String(selectedBatchId)}</CardTitle>
                    {batchShipmentOnHold ? (
                      <Badge tone="warning" className="whitespace-nowrap text-[10px]">
                        Shipment hold
                      </Badge>
                    ) : null}
                  </div>
                  {detailQuery.isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-slate-500" aria-hidden />
                  ) : null}
                </CardHeader>
                <CardBody className="min-h-0 flex-1 space-y-4 overflow-y-auto">
                  {detailQuery.isError ? (
                    <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                      {detailQuery.error.message}
                    </p>
                  ) : null}
                  {detailQuery.data != null ? (
                    <>
                      <div className="space-y-3">
                        <div className="grid gap-4 lg:grid-cols-3 lg:items-start">
                          <dl className="min-w-0 space-y-2 text-sm text-slate-800">
                            <div>
                              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Site</dt>
                              <dd className="mt-0.5">
                                <ManufacturingLocationChip
                                  locationId={detailQuery.data.location_id}
                                  fallbackLabel={detailQuery.data.location_label}
                                />
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ship to</dt>
                              <dd className="mt-0.5 whitespace-pre-wrap">
                                {batchShipToBlock ?? "—"}
                              </dd>
                            </div>
                          </dl>

                          <dl className="min-w-0 space-y-2 text-sm text-slate-800">
                            <div>
                              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Created</dt>
                              <dd className="mt-0.5">{formatCreatedAt(detailQuery.data.date_created)}</dd>
                            </div>
                            <BatchHeaderEditableDd
                              label="Manufacturer ship by"
                              value={toHtmlDateValue(detailQuery.data.mfr_ship_by_date)}
                              display={
                                detailQuery.data.mfr_ship_by_date != null &&
                                detailQuery.data.mfr_ship_by_date.trim() !== "" ? (
                                  formatShortDate(detailQuery.data.mfr_ship_by_date)
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )
                              }
                              inputKind="date"
                              canEdit={hubWritesAllowed}
                              savePending={batchHeaderFieldSavePending("mfr_ship_by_date")}
                              editAriaLabel="Edit manufacturer ship-by date"
                              onSave={
                                hubWritesAllowed
                                  ? (next) =>
                                      saveBatchHeaderField("mfr_ship_by_date", { mfr_ship_by_date: next })
                                  : undefined
                              }
                            />
                            <BatchHeaderEditableDd
                              label="Remark"
                              value={detailQuery.data.mfr_remark ?? ""}
                              display={
                                (detailQuery.data.mfr_remark ?? "").trim() !== "" ? (
                                  <span className="whitespace-pre-wrap break-words">{detailQuery.data.mfr_remark}</span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )
                              }
                              inputKind="multiline"
                              canEdit={hubWritesAllowed}
                              savePending={batchHeaderFieldSavePending("mfr_remark")}
                              editAriaLabel="Edit batch remark"
                              onSave={
                                hubWritesAllowed
                                  ? (next) => saveBatchHeaderField("mfr_remark", { mfr_remark: next })
                                  : undefined
                              }
                            />
                          </dl>

                          <Card className="min-w-0">
                            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 py-3">
                              <CardTitleWithHelp hoverHint="Same card as Production calendar: station WIP, shipped progress, pack out, and shipment glyphs. Only SGS site batches appear on the calendar.">
                                Production calendar
                              </CardTitleWithHelp>
                              {productionCalendarWeekLink != null ? (
                                <Link
                                  className="text-xs font-medium text-sky-700 underline hover:text-sky-900"
                                  to={productionCalendarWeekLink}
                                >
                                  Open calendar week
                                </Link>
                              ) : null}
                            </CardHeader>
                            <CardBody className="pt-0">
                              {calendarEntryQuery.isLoading ? (
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                  Loading calendar entry…
                                </div>
                              ) : null}
                              {calendarEntryQuery.isError ? (
                                <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                                  {calendarEntryQuery.error.message}
                                </p>
                              ) : null}
                              {calendarEntryQuery.data != null && !calendarEntryQuery.isLoading ? (
                                <>
                                  {calendarEntryQuery.data.found === false ? (
                                    <p className="text-sm text-slate-600">This batch was not found.</p>
                                  ) : !calendarEntryQuery.data.eligible ? (
                                    <p className="text-sm text-slate-600">{calendarEntryQuery.data.reason}</p>
                                  ) : (
                                    <BatchCard
                                      batch={calendarEntryQuery.data.batch}
                                      stationsById={calendarStationsById}
                                      hubWritesAllowed={hubWritesAllowed}
                                      alternateAvailabilityByBatchId={new Map()}
                                      embedded
                                    />
                                  )}
                                </>
                              ) : null}
                            </CardBody>
                          </Card>
                        </div>

                        <BatchTeamNotesPanel
                          batchId={selectedBatchId}
                          hubWritesAllowed={hubWritesAllowed}
                        />

                        {patchBatchHeaderMutation.isError ? (
                          <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                            {patchBatchHeaderMutation.error.message}
                          </p>
                        ) : null}
                      </div>

                      <div className="-mx-1 overflow-x-auto">
                        <table className="w-full min-w-[840px] text-left text-xs text-slate-800">
                          <thead className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                            <tr>
                              <th className="px-2 py-1.5">SKU</th>
                              <th className="px-2 py-1.5">Build order</th>
                              <th className="px-2 py-1.5 text-right tabular-nums">Qty</th>
                              <th className="px-2 py-1.5 text-right tabular-nums">Shipped</th>
                              <th className="px-2 py-1.5 text-right tabular-nums">Assembled</th>
                              <th className="px-2 py-1.5">Sales order</th>
                              <th className="px-2 py-1.5">SGS ship by</th>
                              <th className="px-2 py-1.5">Shipment</th>
                              <th className="px-2 py-1.5">TLA build / PO</th>
                              <th className="px-2 py-1.5">ABB labels</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailQuery.data.lines.length === 0 ? (
                              <tr>
                                <td className="px-2 py-3 text-slate-600" colSpan={10}>
                                  No batch lines on this batch.
                                </td>
                              </tr>
                            ) : (
                              detailQuery.data.lines.map((line) => (
                                <tr key={line.batch_item_id} className="border-b border-slate-50 last:border-none">
                                  <td className="min-w-0 px-2 py-1.5 align-top font-mono text-[11px]">
                                    <HoverHintContent
                                      hoverHint={
                                        <MrpSkuCatalogHoverHintBody
                                          sku={line.sku}
                                          hint={skuHoverHints?.[line.sku.trim()]}
                                        />
                                      }
                                      block
                                      cursor="default"
                                      className="min-w-0"
                                    >
                                      <MrpSkuDetailLink
                                        sku={line.sku}
                                        className="block break-all font-mono text-[11px] font-medium text-brand-700 no-underline hover:text-brand-900"
                                      >
                                        <MrpSkuWithUnitLabel
                                          sku={line.sku}
                                          unit={skuHoverHints?.[line.sku.trim()]?.mv_unit}
                                        />
                                      </MrpSkuDetailLink>
                                    </HoverHintContent>
                                  </td>
                                  <td className="px-2 py-1.5 align-top">{line.build_order_number ?? "—"}</td>
                                  <td className="px-2 py-1.5 text-right align-top tabular-nums">
                                    {line.batch_item_quantity}
                                  </td>
                                  <td className="px-2 py-1.5 text-right align-top tabular-nums">
                                    {line.quantity_shipped}
                                  </td>
                                  <td className="px-2 py-1.5 text-right align-top tabular-nums">
                                    {line.quantity_assembled}
                                  </td>
                                  <td className="px-2 py-1.5 align-top">
                                    {line.sales_order_id != null ? (
                                      <HoverHintContent
                                        hoverHint={batchDetailsSalesOrderHoverHint(
                                          detailQuery.data?.sales_order_contexts?.[String(line.sales_order_id)],
                                          line,
                                        )}
                                        block
                                        cursor="default"
                                        className="min-w-0"
                                      >
                                        <SalesOrderDetailLink
                                          salesOrderId={line.sales_order_id}
                                          salesOrderNumber={line.sales_order_number}
                                          className="text-[11px]"
                                        />
                                      </HoverHintContent>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 align-top">{formatShortDate(line.sgs_ship_by_date)}</td>
                                  <td className="px-2 py-1.5 align-top tabular-nums">
                                    {line.shipment_id != null ? (
                                      <ShipmentDetailLink
                                        shipmentId={line.shipment_id}
                                        className="text-[11px] no-underline"
                                        peek={{
                                          ship_date: line.shipment_date_shipped,
                                          ship_to_label: batchShipToBlock,
                                        }}
                                      >
                                        {line.shipment_id}
                                      </ShipmentDetailLink>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 align-top text-[11px]">
                                    {line.tla_build_order_number != null ? line.tla_build_order_number : "—"}
                                    {line.tla_purchase_order_number != null ? (
                                      <span className="text-slate-600"> · PO {line.tla_purchase_order_number}</span>
                                    ) : null}
                                  </td>
                                  <td className="px-2 py-1.5 align-top">
                                    {isAbbPddLabelSku(line.sku) ? (
                                      <div className="space-y-1">
                                        {hubWritesAllowed ? (
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="secondary"
                                            className="h-7 whitespace-nowrap"
                                            onClick={() => setPddAssignLine(line)}
                                          >
                                            {line.has_pdd_labels ? "Replace ABB labels" : "Assign ABB labels"}
                                          </Button>
                                        ) : line.has_pdd_labels ? (
                                          <span className="text-slate-600">Assigned</span>
                                        ) : (
                                          <span className="text-slate-500">Not assigned</span>
                                        )}
                                        {line.has_pdd_labels && (line.pdd_catalog ?? line.pdd_sku) ? (
                                          <div className="text-[10px] text-slate-500">
                                            {line.pdd_catalog ?? line.pdd_sku}
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : detailQuery.isLoading ? (
                    <p className="text-sm text-slate-600">Loading batch…</p>
                  ) : null}
                </CardBody>
              </>
            )}
          </Card>

          {selectedBatchId != null && detailQuery.data != null ? (
            <Card>
              <CardHeader className="p-0">
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={serialsOpen}
                  className="flex w-full cursor-pointer flex-row flex-wrap items-center justify-between gap-2 px-5 py-4 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-inset"
                  onClick={() => setSerialsOpen((open) => !open)}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSerialsOpen((open) => !open);
                    }
                  }}
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <CardTitle className="mb-0 text-base">Serials</CardTitle>
                    <span
                      className="inline-flex shrink-0"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <HoverHintWithGlyph hoverHint="Device label, current station, IMEI, wireless portal org, group, and match status, finished SKU, and box label serials linked to this batch through batch lines. Portal match confirms portal IMEI and serial match the hub device, portal Status is Shipped, and ShipmentDate is today or earlier. Device label opens hub device lookup; the external link opens the wireless portal device page when an IMEI is present. Refresh portal re-reads org, group, and match from the live wireless portal database." />
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {serialsHasImei && !import.meta.env.DEV ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-8"
                        disabled={serialsPortalQuery.isFetching}
                        onClick={(event) => {
                          event.stopPropagation();
                          void serialsPortalQuery.refetch();
                        }}
                      >
                        <RefreshCw
                          className={cn(
                            "h-3.5 w-3.5 shrink-0",
                            serialsPortalQuery.isFetching && "animate-spin",
                          )}
                          aria-hidden
                        />
                        Refresh portal
                      </Button>
                    ) : null}
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-700">
                      {serialsOpen ? (
                        <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      )}
                      {serialsOpen
                        ? "Hide serials"
                        : `Show serials (${String(detailQuery.data.serial_count ?? 0)})`}
                    </span>
                  </div>
                </div>
              </CardHeader>
              {serialsOpen ? (
                <CardBody className="p-0">
                  {serialsQuery.isPending && serialsQuery.data == null ? (
                    <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-600">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Loading serials…
                    </div>
                  ) : null}
                  {serialsQuery.isError ? (
                    <p className="mx-4 my-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                      {serialsQuery.error.message}
                    </p>
                  ) : null}
                  {serialsQuery.data != null ? (
                    <div>
                      {displaySerialRows.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-slate-600">No serials linked to this batch yet.</p>
                      ) : (
                        <div className="max-h-[min(28rem,50vh)] overflow-auto [scrollbar-gutter:stable]">
                          <table className="w-full min-w-[56rem] table-fixed border-separate border-spacing-0 text-left text-xs text-slate-800">
                            <colgroup>
                              <col className="w-[9.5rem]" />
                              <col className="w-[6.5rem]" />
                              <col className="w-[8rem]" />
                              <col />
                              <col />
                              <col className="w-[8.5rem]" />
                              <col className="w-[8.5rem]" />
                              <col className="w-[8rem]" />
                            </colgroup>
                            <thead className="sticky top-0 z-10 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                              <tr>
                                <CompactSortTh
                                  column="device_label"
                                  sortKey={serialsSortKey}
                                  sortDir={serialsSortDir}
                                  onSort={onSerialsSort}
                                  className="sticky top-0 border-b border-slate-200 px-3 py-1.5 shadow-[0_1px_0_0_rgb(226_232_240)]"
                                >
                                  Device label
                                </CompactSortTh>
                                <CompactSortTh
                                  column="station"
                                  sortKey={serialsSortKey}
                                  sortDir={serialsSortDir}
                                  onSort={onSerialsSort}
                                  className="sticky top-0 border-b border-slate-200 px-2 py-1.5 shadow-[0_1px_0_0_rgb(226_232_240)]"
                                >
                                  Station
                                </CompactSortTh>
                                <CompactSortTh
                                  column="imei"
                                  sortKey={serialsSortKey}
                                  sortDir={serialsSortDir}
                                  onSort={onSerialsSort}
                                  className="sticky top-0 border-b border-slate-200 px-2 py-1.5 shadow-[0_1px_0_0_rgb(226_232_240)]"
                                >
                                  IMEI
                                </CompactSortTh>
                                <CompactSortTh
                                  column="portal_org"
                                  sortKey={serialsSortKey}
                                  sortDir={serialsSortDir}
                                  onSort={onSerialsSort}
                                  className="sticky top-0 border-b border-slate-200 px-2 py-1.5 shadow-[0_1px_0_0_rgb(226_232_240)]"
                                >
                                  Portal org
                                </CompactSortTh>
                                <CompactSortTh
                                  column="portal_group"
                                  sortKey={serialsSortKey}
                                  sortDir={serialsSortDir}
                                  onSort={onSerialsSort}
                                  className="sticky top-0 border-b border-slate-200 px-2 py-1.5 shadow-[0_1px_0_0_rgb(226_232_240)]"
                                >
                                  Portal group
                                </CompactSortTh>
                                <CompactSortTh
                                  column="portal_match"
                                  sortKey={serialsSortKey}
                                  sortDir={serialsSortDir}
                                  onSort={onSerialsSort}
                                  className="sticky top-0 whitespace-nowrap border-b border-slate-200 px-2 py-1.5 shadow-[0_1px_0_0_rgb(226_232_240)]"
                                >
                                  Portal match
                                </CompactSortTh>
                                <CompactSortTh
                                  column="finished_sku"
                                  sortKey={serialsSortKey}
                                  sortDir={serialsSortDir}
                                  onSort={onSerialsSort}
                                  className="sticky top-0 border-b border-slate-200 px-2 py-1.5 shadow-[0_1px_0_0_rgb(226_232_240)]"
                                >
                                  Finished SKU
                                </CompactSortTh>
                                <CompactSortTh
                                  column="box_label"
                                  sortKey={serialsSortKey}
                                  sortDir={serialsSortDir}
                                  onSort={onSerialsSort}
                                  className="sticky top-0 border-b border-slate-200 px-3 py-1.5 shadow-[0_1px_0_0_rgb(226_232_240)]"
                                >
                                  Box label
                                </CompactSortTh>
                              </tr>
                            </thead>
                            <tbody>
                            {sortedDisplaySerialRows.map((row) => {
                              const rowHasImei = (row.imei ?? "").trim() !== "";
                              return (
                              <tr
                                key={row.serial_fci_id}
                                className="border-b border-slate-50 last:border-none"
                              >
                                <td className="border-b border-slate-50 px-3 py-1.5 align-top font-mono text-[11px]">
                                  {row.device_label_serial != null &&
                                  row.device_label_serial.trim() !== "" ? (
                                    <span className="inline-flex items-center gap-1">
                                      <Link
                                        className="font-medium text-brand-700 hover:text-brand-900"
                                        to={`/manufacturing/device-lookup?serial=${encodeURIComponent(row.device_label_serial)}`}
                                      >
                                        {row.device_label_serial}
                                      </Link>
                                      {row.imei != null && row.imei.trim() !== "" ? (
                                        <a
                                          href={wirelessPortalDeviceDetailsUrl(row.imei)}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="inline-flex shrink-0 rounded text-slate-400 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                                          aria-label={`Open wireless portal for ${row.device_label_serial}`}
                                        >
                                          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                                        </a>
                                      ) : null}
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td className="border-b border-slate-50 px-2 py-1.5 align-top text-[11px]">
                                  {row.station_name != null && row.station_name.trim() !== ""
                                    ? row.station_name
                                    : "—"}
                                </td>
                                <td className="border-b border-slate-50 px-2 py-1.5 align-top font-mono text-[11px]">
                                  {row.imei ?? "—"}
                                </td>
                                <td
                                  className={cn(
                                    "border-b border-slate-50 px-2 py-1.5 align-top transition-[filter] duration-200",
                                    portalColumnsPending && "blur-sm select-none text-slate-400",
                                  )}
                                >
                                  {batchSerialPortalFieldCell(row.wireless_portal_org_name, {
                                    rowHasImei,
                                    pending: portalColumnsPending,
                                    failed: portalColumnsFailed,
                                    errorMessage: portalColumnsErrorMessage,
                                    fieldLabel: "Portal org",
                                  })}
                                </td>
                                <td
                                  className={cn(
                                    "border-b border-slate-50 px-2 py-1.5 align-top transition-[filter] duration-200",
                                    portalColumnsPending && "blur-sm select-none text-slate-400",
                                  )}
                                >
                                  {batchSerialPortalFieldCell(row.wireless_portal_group_names, {
                                    rowHasImei,
                                    pending: portalColumnsPending,
                                    failed: portalColumnsFailed,
                                    errorMessage: portalColumnsErrorMessage,
                                    fieldLabel: "Portal group",
                                  })}
                                </td>
                                <td
                                  className={cn(
                                    "border-b border-slate-50 px-2 py-1.5 align-top transition-[filter] duration-200",
                                    portalColumnsPending && "blur-sm select-none text-slate-400",
                                  )}
                                >
                                  {batchSerialPortalAlignmentCell(
                                    portalAlignmentBySerialFciId[String(row.serial_fci_id)],
                                    {
                                      rowHasImei,
                                      pending: portalColumnsPending,
                                      failed: portalColumnsFailed,
                                      errorMessage: portalColumnsErrorMessage,
                                    },
                                  )}
                                </td>
                                <td className="border-b border-slate-50 px-2 py-1.5 align-top font-mono text-[11px]">
                                  <span className="inline-flex items-start gap-1">
                                    <span>
                                      {row.finished_sku != null && row.finished_sku.trim() !== ""
                                        ? row.finished_sku
                                        : "—"}
                                    </span>
                                    {batchSerialFinishedSkuMismatch(row.batch_line_sku, row.finished_sku) ? (
                                      <HoverHintTrigger
                                        trigger={
                                          <CircleAlert
                                            className="h-3.5 w-3.5 shrink-0 text-red-600"
                                            aria-label="Finished SKU does not match batch line SKU"
                                          />
                                        }
                                      >
                                        Batch line SKU is{" "}
                                        {row.batch_line_sku != null && row.batch_line_sku.trim() !== ""
                                          ? row.batch_line_sku
                                          : "—"}
                                        .
                                      </HoverHintTrigger>
                                    ) : null}
                                  </span>
                                </td>
                                <td className="border-b border-slate-50 px-3 py-1.5 align-top font-mono text-[11px]">
                                  {row.box_serial ?? "—"}
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        </div>
                      )}
                    </div>
                  ) : null}
                </CardBody>
              ) : null}
            </Card>
          ) : null}
          </div>
        </div>
      )}
      {pddAssignLine != null && selectedBatchId != null ? (
        <AssignAbbPddLabelsModal
          open
          onClose={() => setPddAssignLine(null)}
          batchId={selectedBatchId}
          batchItemId={pddAssignLine.batch_item_id}
          sku={pddAssignLine.sku}
          quantity={pddAssignLine.batch_item_quantity}
          alreadyAssigned={pddAssignLine.has_pdd_labels}
        />
      ) : null}
    </div>
  );
}
